import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import BlogCard from "./components/BlogCard";
import SEOHead from "@/components/feature/SEOHead";
import { blogPosts } from "../../mocks/blogData";

const filters = ["All", "Jordan", "Aidan", "Lore"];

const loreSchema = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${import.meta.env.VITE_SITE_URL || ""}/lore#webpage`,
    "url": `${import.meta.env.VITE_SITE_URL || ""}/lore`,
    "name": "Lore Archive | The Wraith Chronicles | Character Histories & Untold Stories",
    "description":
      "Explore the Lore Archive — character histories, field transmissions, and pre-outbreak stories from The Wraith's post-apocalyptic world. Discover Jordan Reyes and Aidan Lee before Green Monday.",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${import.meta.env.VITE_SITE_URL || ""}/#website`,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "The Wraith Lore Archive",
    "description":
      "Character histories, field transmissions, and untold stories from The Wraith's post-apocalyptic world — Jordan Reyes and Aidan Lee before Green Monday.",
    "url": `${import.meta.env.VITE_SITE_URL || ""}/lore`,
    "inLanguage": "en",
    "blogPost": blogPosts.map((p) => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "description": p.excerpt,
      "url": `${import.meta.env.VITE_SITE_URL || ""}/lore/${p.slug}`,
      "image": p.coverImage,
      "datePublished": p.date,
    })),
  },
];

export default function BlogPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("All");

  const filtered =
    activeFilter === "All"
      ? blogPosts
      : blogPosts.filter((p) => p.characterTag === activeFilter || p.characterTag.toLowerCase() === activeFilter.toLowerCase());

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title="Lore Archive | The Wraith Chronicles | Character Histories & Untold Stories"
        description="Explore the Lore Archive — character histories, field transmissions, and pre-outbreak stories from The Wraith's post-apocalyptic world. Discover Jordan Reyes and Aidan Lee before Green Monday."
        keywords="The Wraith lore, Jordan Reyes backstory, Aidan Lee backstory, post-apocalyptic character history, Green Monday origins"
        canonical="/lore"
        ogTitle="The Wraith Lore Archive — Character Histories & Untold Stories"
        ogDescription="Character histories. Untold stories. Field transmissions from before the world ended."
        schema={loreSchema}
      />

      <Navbar />

      {/* Hero Header */}
      <header className="relative w-full pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b7420a9dda8ce19bbeb9c926d20294fd.jpg"
            alt="Lore Archive background"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/40 to-[#0A0A0A]" />
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <span className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.4em] uppercase block mb-6">
            [ Transmission Archive — Classified ]
          </span>
          <h1 className="font-['Special_Elite'] text-white text-5xl md:text-7xl mb-4">
            The <span className="text-[#C8922A]">Lore</span> Archive
          </h1>
          <p className="font-['Rajdhani'] text-[#777] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Character histories. Untold stories. Field transmissions from before the world ended.
            Read the lives they lived before the apocalypse rewrote them.
          </p>

          <div className="mt-10 flex items-center justify-center gap-6">
            <div className="h-px flex-1 max-w-32 bg-[#8B1A1A]/40" />
            <span className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-[0.3em] uppercase">
              {blogPosts.length} {blogPosts.length === 1 ? "Entry" : "Entries"} Recovered
            </span>
            <div className="h-px flex-1 max-w-32 bg-[#8B1A1A]/40" />
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <section className="w-full px-6 mb-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-1 border-b border-[#1E1E1E] pb-0">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`font-['Share_Tech_Mono'] text-xs tracking-[0.2em] uppercase px-5 py-3 border-b-2 transition-all duration-300 cursor-pointer whitespace-nowrap -mb-px ${
                  activeFilter === f
                    ? "text-[#C8922A] border-[#C8922A]"
                    : "text-[#444] border-transparent hover:text-[#777]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <main className="w-full px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}

              {/* Coming Soon Slot */}
              <div className="border border-dashed border-[#222] flex flex-col items-center justify-center py-16 px-8 text-center">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <i className="ri-time-line text-[#333] text-2xl" />
                </div>
                <div className="font-['Share_Tech_Mono'] text-[#333] text-xs tracking-widest uppercase mb-2">
                  More Entries
                </div>
                <p className="font-['Rajdhani'] text-[#2C2C2C] text-sm">
                  New stories are being recovered from the field. Check back soon.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-search-line text-[#333] text-3xl" />
              </div>
              <p className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-widest uppercase">
                No entries found for this filter
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Back to Home */}
      <div className="w-full px-6 pb-16 border-t border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto pt-10 flex items-center justify-between flex-wrap gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 font-['Share_Tech_Mono'] text-[#555] hover:text-[#C8922A] text-xs tracking-[0.2em] uppercase transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm" />
            Back to Main Site
          </button>
          <span className="font-['Share_Tech_Mono'] text-[#2C2C2C] text-xs tracking-widest uppercase">
            [ Wraith &amp; The Chef — Lore Archive ]
          </span>
        </div>
      </div>

      <Footer />
    </div>
  );
}
